//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ClientSamplingCampaignBvIdsResponse,
  ClientSamplingCampaignsResponse,
  GetClientSamplingCampaignBvIdsResponse,
  GetClientSamplingCampaignByIdRequest,
  GetClientSamplingCampaignProductsResponse,
  GetClientSamplingCampaignReviewsResponse,
  GetClientSamplingCampaignsRequest,
  GetClientSamplingCampaignsResponse,
  GetReviewCompletionRateRequest,
  GetReviewCompletionRateResponse,
  GetSamplingCampaignPostSurveyRequest,
  GetSamplingCampaignProductsRequest,
  GetSamplingCampaignResponse,
  GetSamplingCampaignReviewsRequest,
  GetSyndicationDestinationsRequest,
  GetSyndicationDestinationsResponse,
  SamplingCampaign,
  SamplingCampaignExportRequest,
  SamplingCampaignPostSurveyResponse,
  SamplingCampaignProductsResponse,
  SamplingCampaignReviewsResponse,
  SyndicationDestinationsResponse,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiModel';
import {
  deserializeCampaign,
  deserializeCampaignProduct,
  deserializeCampaignReview,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiUtils';
import { SamplingCampaignExportPath } from '@campaigns/constants/sampling/SamplingCampaignExportConstants';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CAMPAIGNS_SAMPLING_GATEWAY_DEFAULT_ENDPOINT = '/api/v1/clients/campaigns';

const campaignsSamplingGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${CAMPAIGNS_SAMPLING_GATEWAY_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );

/**
 * Gets all campaigns for a specific client
 *
 * GET /api/v1/campaigns
 */
export const getClientCampaigns = async (
  req: GetClientSamplingCampaignsRequest,
  init?: RequestInit,
): Promise<GetClientSamplingCampaignsResponse> => {
  const params = makeParams(req);

  const res = await campaignsSamplingGatewayFetch(`?${params}`, {
    ...init,
    method: 'GET',
  });

  const campaignsResponse = await res.json<ClientSamplingCampaignsResponse>(
    (res) => ({
      data: {
        ...res.data,
        campaigns: res.data.campaigns.map((campaign) =>
          deserializeCampaign(campaign),
        ),
      },
    }),
  );

  return campaignsResponse.data;
};

/**
 * Gets all campaign IDs for a specific client
 *
 * GET /api/v1/clients/campaigns/bv-campaign-ids
 */
export const getClientCampaignBvIds = async (
  init?: RequestInit,
): Promise<GetClientSamplingCampaignBvIdsResponse> => {
  const res = await campaignsSamplingGatewayFetch('/bv-campaign-ids', {
    ...init,
    method: 'GET',
  });

  const campaignIdsResponse =
    await res.json<ClientSamplingCampaignBvIdsResponse>();

  return campaignIdsResponse.data;
};

/**
 * Gets a campaign for a specific client
 *
 * GET /api/v1/campaign/:campaignId
 */

export const getClientCampaign = async (
  req: GetClientSamplingCampaignByIdRequest,
  init?: RequestInit,
): Promise<SamplingCampaign> => {
  const res = await campaignsSamplingGatewayFetch(`/${req.campaignId}`, {
    ...init,
    method: 'GET',
  });

  const campaignResponse = await res.json<GetSamplingCampaignResponse>(
    (res) => ({
      data: deserializeCampaign(res.data),
    }),
  );

  return campaignResponse.data;
};

/**
 * Gets products for a specific campaign
 *
 * GET /api/v1/campaign/:campaignId/products
 */

export const getClientCampaignProducts = async (
  req: GetSamplingCampaignProductsRequest,
  init?: RequestInit,
): Promise<SamplingCampaignProductsResponse> => {
  const { campaignId, ...rest } = req;
  const params = makeParams(rest);

  const res = await campaignsSamplingGatewayFetch(
    `/${campaignId}/products?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignProductsResponse =
    await res.json<GetClientSamplingCampaignProductsResponse>((res) => ({
      data: {
        ...res.data,
        products: res.data.products?.map((product) =>
          deserializeCampaignProduct(product),
        ),
      },
    }));

  return campaignProductsResponse.data;
};

/**
 * Gets reviews for a specific campaign
 *
 * GET /api/v1/campaign/:campaignId/reviews
 */

export const getClientCampaignReviews = async (
  req: GetSamplingCampaignReviewsRequest,
  init?: RequestInit,
): Promise<SamplingCampaignReviewsResponse> => {
  const { campaignId, ...rest } = req;
  const params = makeParams(rest);

  const res = await campaignsSamplingGatewayFetch(
    `/${campaignId}/reviews?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignReviewsResponse =
    await res.json<GetClientSamplingCampaignReviewsResponse>((res) => ({
      data: {
        ...res.data,
        reviews: res.data.reviews?.map((product) =>
          deserializeCampaignReview(product),
        ),
      },
    }));

  return campaignReviewsResponse.data;
};

/**
 * Gets syndication destinations for a specific campaign
 *
 * GET /api/v1/campaign/:campaignId/syndication/destinations
 */

export const getClientCampaignSyndicationDestinations = async (
  req: GetSyndicationDestinationsRequest,
  init?: RequestInit,
): Promise<SyndicationDestinationsResponse> => {
  const { campaignId, ...rest } = req;
  const params = makeParams(rest);

  const res = await campaignsSamplingGatewayFetch(
    `/${campaignId}/syndication/destinations?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  return (await res.json<GetSyndicationDestinationsResponse>()).data;
};

/**
 * Gets post survey for a specific campaign
 *
 * GET /api/v1/campaign/:campaignId/surveys/aggregated-results
 */

export const getClientCampaignPostSurvey = async (
  req: GetSamplingCampaignPostSurveyRequest,
  init?: RequestInit,
): Promise<SamplingCampaignPostSurveyResponse> => {
  const { campaignId } = req;

  const res = await campaignsSamplingGatewayFetch(
    `/${campaignId}/surveys/aggregated-results`,
    {
      ...init,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return await res.json<SamplingCampaignPostSurveyResponse>();
};

/**
 * Exports sampling campaign data
 *
 * GET /api/v1/clients/campaigns/:campaignId/export (for 'all' type - returns ZIP)
 * GET /api/v1/clients/campaigns/:campaignId/reviews/export
 * GET /api/v1/clients/campaigns/:campaignId/social/export
 * GET /api/v1/clients/campaigns/:campaignId/products/export
 * GET /api/v1/clients/campaigns/:campaignId/surveys/aggregated-results/export
 */
export const exportSamplingCampaignCsv = async (
  req: SamplingCampaignExportRequest,
  init?: RequestInit,
): Promise<Blob> => {
  const { campaignId, ecommerceSessionType, exportType } = req;

  const exportPath = `/${campaignId}${SamplingCampaignExportPath[exportType]}`;

  const queryParams = ecommerceSessionType
    ? makeParams({ ecommerceSessionType })
    : '';
  const fullPath = queryParams ? `${exportPath}?${queryParams}` : exportPath;

  const res = await campaignsSamplingGatewayFetch(fullPath, {
    ...init,
    method: 'GET',
  });

  return res.blob();
};

export const getReviewCompletionRate = async (
  req: GetReviewCompletionRateRequest,
  init?: RequestInit,
): Promise<GetReviewCompletionRateResponse> => {
  const url = `/${req.campaignId}/external-reviews/aggregated-results`;
  const response = await campaignsSamplingGatewayFetch(url, init);
  return response.json();
};
